import { MessageSquare, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreOverrideDialog } from './score-override-dialog';
import { scrollToTranscript, verdictColor } from './scoring-utils';
import type { QuestionScoreRow } from './types';

interface EligibilityResultsProps {
  questions: QuestionScoreRow[];
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
}

export function EligibilityResults({
  questions,
  orgId,
  deptId,
  jobId,
  applicationId
}: EligibilityResultsProps): React.ReactNode {
  const { t } = useTranslation();
  const [editingQuestion, setEditingQuestion] = useState<QuestionScoreRow | null>(null);

  if (questions.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{t('scoring.eligibility')}</h2>
      <div className="flex flex-col gap-2">
        {questions.map((q) => (
          <div
            key={q.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm border-l-4 border-l-[var(--color-section-blue)]"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium">{q.questionText}</p>
              <button
                type="button"
                className="group/score shrink-0 flex items-center gap-1 cursor-pointer rounded px-1 -mx-1 hover:bg-muted/50"
                onClick={() => {
                  setEditingQuestion(q);
                }}
              >
                <span className={`text-sm font-medium ${verdictColor(q.verdict)}`}>
                  {t(`scoring.verdicts.${q.verdict ?? 'unsure'}` as 'scoring.verdicts.pass')}
                </span>
                <Pencil className="size-3 text-muted-foreground opacity-0 group-hover/score:opacity-100 transition-opacity" />
              </button>
            </div>
            {(q.reasoning || q.evidence) && (
              <div className="mt-2 flex flex-col gap-1">
                {q.reasoning && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">{t('scoring.reasoning')}:</span> {q.reasoning}
                  </p>
                )}
                {q.evidence && (
                  <p className="text-sm italic text-muted-foreground">&ldquo;{q.evidence}&rdquo;</p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                scrollToTranscript(q.questionId);
              }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <MessageSquare className="size-3" />
              {t('scoring.viewInTranscript')}
            </button>
          </div>
        ))}
      </div>

      {editingQuestion && (
        <ScoreOverrideDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditingQuestion(null);
          }}
          questionScoreId={editingQuestion.id}
          questionText={editingQuestion.questionText}
          currentValue={editingQuestion.verdict ?? 'unsure'}
          mode="eligibility"
          orgId={orgId}
          deptId={deptId}
          jobId={jobId}
          applicationId={applicationId}
        />
      )}
    </div>
  );
}
